// TODO(969c-followup): billing has a boundary-respecting COPY of this hook at
// `@/billing/services/use-claim-diagnosis` (it can't cross-import patients/).
// Drift risk: a fix here won't reach billing. Consolidate into a single
// `@/shared/` ICD-10 search hook and have both domains consume it.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ConditionCode } from '@/types/patient-chart-api';

/**
 * Master data — ICD-10 Diagnosis code typeahead for the Conditions
 * (Problems) Add flow (PROD-827 chart write surfaces).
 *
 * Wire (Flutter `common_data_source_impl.dart` `searchDiagnosis()`):
 *
 *   GET /api/Master/DiagnosisCodes?system=ICD-10&searchQuery={query}
 *       → ConditionCode[]  (CodeWordCase + PascalCase `Definition`)
 *
 * Curl-verified by Andrew 2026-05-19. Each result row carries a
 * **PascalCase** `Definition` field (the long-form description)
 * alongside the lowercase `display` (the short form). Both ship
 * verbatim into the CreateCondition wire body.
 *
 * **Stale-request safety** (same four-layer defense as
 * `useAllergyCodeSearch`):
 * - `signal` forwarded — React Query auto-cancels on key change.
 * - `enabled` flips false below `MIN_QUERY_LENGTH`.
 * - Dialog's result panel gates on query length too.
 * - `placeholderData` only affects which `data` is exposed during
 *   refetches; doesn't bypass cancellation.
 */

const MIN_QUERY_LENGTH = 2;
/** ICD-10 is the default catalog. Future: ICD-11 / SNOMED toggle. */
const DEFAULT_SYSTEM = 'ICD-10';

export function useDiagnosisCodeSearch(
  query: string,
  enabled = true,
  system: string = DEFAULT_SYSTEM,
) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['master-diagnosis-codes', system, trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ system, searchQuery: trimmed });
      return get<ConditionCode[]>(
        `/api/Master/DiagnosisCodes?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
